import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../api";
import PushSettings from "../components/PushSettings";
import Tags from "./Tags";
import ImportPage from "./Import";
import Consents from "./Consents";
import CalendarSettings from "./CalendarSettings";
import AutomationSettings from "./AutomationSettings";
import GreenWhatsApp from "./GreenWhatsApp";
import SignatureEditor from "./SignatureEditor";
import SmsSettings from "./SmsSettings";
import ConsentAdmin from "./ConsentAdmin";

const PEC_EMPTY = { provider: "pec_aruba", address: "", username: "", password: "", forward_to: "", company_id: "", forward_enabled: true };
const GMAIL_EMPTY = { provider: "gmail", address: "", oauth_client_id: "", oauth_client_secret: "", forward_enabled: false };
const GMAIL_REDIRECT = `${window.location.origin}/api/mailboxes/gmail/callback`;

const TABS = [
  { id: "email", label: "Caselle email" },
  { id: "notifiche", label: "Notifiche 🔔" },
  { id: "automazioni", label: "Automazioni" },
  { id: "sms", label: "SMS / SMShosting" },
  { id: "calendario", label: "Calendario" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "contatti", label: "Contatti" },
  { id: "esclusi", label: "Mittenti esclusi" },
  { id: "margini", label: "Margini" },
  { id: "etichette", label: "Etichette" },
  { id: "import", label: "Import" },
  { id: "consensi", label: "Consensi privacy" },
  { id: "consensi-gdpr", label: "Consensi GDPR (opt-in)" },
  { id: "backup", label: "Backup / Dati" },
  { id: "cancellazioni", label: "Registro cancellazioni" },
];

export default function Settings() {
  const loc = useLocation();
  const [tab, setTab] = useState("email");
  const [rules, setRules] = useState([]);
  const [value, setValue] = useState("");
  const [matchType, setMatchType] = useState("email");
  const [margin, setMargin] = useState({ hourly_cost: 50, minutes_per_document: 15 });
  const [boxes, setBoxes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pec, setPec] = useState(PEC_EMPTY);
  const [gmail, setGmail] = useState(GMAIL_EMPTY);
  const [wa, setWa] = useState({ phone_number_id: "", access_token: "", verify_token: "" });
  const [waSaved, setWaSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [gmailPaused, setGmailPaused] = useState(false);

  const load = () => api.get("/exclusion-rules").then((r) => setRules(r.data)).catch(() => {});
  const loadBoxes = () => api.get("/mailboxes").then((r) => setBoxes(r.data)).catch(() => {});
  const loadWa = () => api.get("/funnels/whatsapp-config").then((r) => { setWaSaved(r.data); setWa({ phone_number_id: r.data.phone_number_id || "", access_token: "", verify_token: r.data.verify_token || "" }); }).catch(() => {});
  useEffect(() => { load(); loadBoxes(); loadWa(); }, []);
  useEffect(() => { api.get("/invoices/config").then((r) => setMargin(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const t = params.get("tab");
    if (t && TABS.some((x) => x.id === t)) setTab(t);
    const p = params.get("gmail");
    if (p === "ok") { setNotice("Gmail collegato con successo."); setTab("email"); loadBoxes(); }
    else if (p === "error") { setNotice("Collegamento Gmail non riuscito. Riprova."); setTab("email"); }
  }, [loc.search]);

  const saveWa = async () => {
    setBusy(true);
    try { const r = await api.put("/funnels/whatsapp-config", wa); setWaSaved(r.data); setWa((w) => ({ ...w, access_token: "" })); setNotice("Configurazione WhatsApp salvata."); }
    catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  // PEC
  const addPec = async () => {
    if (!pec.address || !pec.password || !pec.forward_to) { alert("Indirizzo PEC, password e indirizzo di inoltro sono obbligatori"); return; }
    setBusy(true);
    try {
      const payload = { ...pec }; if (!payload.company_id) delete payload.company_id;
      await api.post("/mailboxes", payload); setPec(PEC_EMPTY); loadBoxes();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const connectPec = async (id) => { try { await api.post(`/mailboxes/${id}/connect`); loadBoxes(); alert("Casella collegata."); } catch (e) { alert(e.response?.data?.detail || e.message); loadBoxes(); } };
  const syncPec = async (id) => { try { const r = await api.post(`/mailboxes/${id}/sync`); alert(`Importati ${r.data.importati}, inoltrati ${r.data.inoltrati}.`); loadBoxes(); } catch (e) { alert(e.response?.data?.detail || e.message); loadBoxes(); } };
  const removeBox = async (id) => { if (!window.confirm("Rimuovere questa casella?")) return; try { await api.delete(`/mailboxes/${id}`); loadBoxes(); } catch (e) { alert("Impossibile eliminare: " + (e.response?.status === 401 ? "sessione scaduta, esci e rientra." : (e.response?.data?.detail || e.message))); loadBoxes(); } };

  // Gmail
  const addGmail = async () => {
    if (!gmail.address || !gmail.oauth_client_id || !gmail.oauth_client_secret) { alert("Indirizzo Gmail, Client ID e Client Secret sono obbligatori"); return; }
    setBusy(true);
    try { await api.post("/mailboxes", gmail); setGmail(GMAIL_EMPTY); loadBoxes(); }
    catch (e) { alert(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const connectGmail = async (id) => {
    try {
      const r = await api.get(`/mailboxes/${id}/gmail/authorize`, { params: { redirect_uri: GMAIL_REDIRECT } });
      window.location.href = r.data.auth_url;
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const saveMargin = async () => {
    try { await api.put("/invoices/config", { hourly_cost: Number(margin.hourly_cost), minutes_per_document: Number(margin.minutes_per_document), minutes_per_email: Number(margin.minutes_per_email ?? 5), minutes_per_task: Number(margin.minutes_per_task ?? 20) }); alert("Parametri salvati. Margini ricalcolati."); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const addRule = async () => { if (!value.trim()) return; try { const r = await api.post("/exclusion-rules", { value: value.trim(), match_type: matchType }); setValue(""); load(); if (r.data.emails_eliminate) alert(`Regola aggiunta. Eliminate ${r.data.emails_eliminate} email passate di questo mittente.`); } catch (e) { alert(e.response?.data?.detail || e.message); } };
  const removeRule = async (id) => { await api.delete(`/exclusion-rules/${id}`); load(); };

  const pecBoxes = boxes.filter((b) => String(b.provider).startsWith("pec"));
  const gmailBoxes = boxes.filter((b) => b.provider === "gmail");
  const [senderId, setSenderId] = useState("");
  useEffect(() => { api.get("/mailboxes/sender-config").then((r) => setSenderId(r.data.mailbox_id || "")).catch(() => {}); }, []);
  const saveSender = async (id) => {
    if (!id) return;
    setSenderId(id);
    try { const r = await api.put("/mailboxes/sender-config", { mailbox_id: id }); setNotice(`Mittente per invii automatici: ${r.data.address}`); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  useEffect(() => { api.get("/mailboxes/gmail/pause").then((r) => setGmailPaused(!!r.data.paused)).catch(() => {}); }, []);
  const toggleGmailPause = async () => {
    try {
      const r = await api.post("/mailboxes/gmail/pause", { paused: !gmailPaused });
      setGmailPaused(!!r.data.paused);
      setNotice(r.data.paused ? "Gmail sospeso in questo ambiente." : "Gmail riattivato in questo ambiente.");
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div>
      <h1 className="page-title">Impostazioni</h1>
      <div className="toolbar" style={{ gap: 8, marginBottom: 16 }}>
        {TABS.map((t) => (
          <button key={t.id} className={"btn" + (tab === t.id ? " primary" : "")} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>
      {notice && <p className="muted" data-testid="settings-notice" style={{ color: "#7dd3fc" }}>{notice}</p>}

      {tab === "email" && (
        <>
          <SignatureEditor />
          <div className="panel">
            <h3>Casella Gmail dello studio</h3>
            <div style={{ padding: "8px 12px", borderRadius: 8, marginBottom: 10, background: gmailPaused ? "rgba(250,204,21,.08)" : "rgba(148,163,184,.06)", border: gmailPaused ? "1px solid rgba(250,204,21,.35)" : "1px solid var(--border)" }} data-testid="gmail-pause-banner">
              {gmailPaused ? (
                <span style={{ color: "#facc15", fontSize: 13 }}>⏸ <b>Gmail sospeso in questo ambiente</b>: nessuna sincronizzazione né invio, per lasciare tutta la quota Google alla produzione.</span>
              ) : (
                <span className="muted" style={{ fontSize: 13 }}>Gmail attivo in questo ambiente (sync e invii).</span>
              )}{" "}
              <button className="btn sm" style={{ marginLeft: 8 }} onClick={toggleGmailPause} data-testid="gmail-pause-toggle">{gmailPaused ? "♻ Riattiva Gmail" : "⏸ Sospendi Gmail qui"}</button>
            </div>
            <p className="muted">Collega il tuo Gmail per portare le email in "Da gestire" e inviare le campagne. Le credenziali OAuth si creano una volta su Google Cloud Console (Gmail API → OAuth client ID, tipo Web). Aggiungi questo <strong>URI di reindirizzamento</strong> autorizzato:</p>
            <p><code data-testid="gmail-redirect-uri">{GMAIL_REDIRECT}</code></p>
            <div className="grid-2">
              <div className="field"><label>Indirizzo Gmail</label><input placeholder="studio@gmail.com" value={gmail.address} onChange={(e) => setGmail({ ...gmail, address: e.target.value })} data-testid="gmail-address" /></div>
              <div className="field"><label>Client ID</label><input value={gmail.oauth_client_id} onChange={(e) => setGmail({ ...gmail, oauth_client_id: e.target.value })} data-testid="gmail-client-id" /></div>
            </div>
            <div className="field"><label>Client Secret</label><input type="password" value={gmail.oauth_client_secret} onChange={(e) => setGmail({ ...gmail, oauth_client_secret: e.target.value })} data-testid="gmail-client-secret" /></div>
            <button className="btn primary" onClick={addGmail} disabled={busy} data-testid="gmail-add-btn">{busy ? "Salvataggio…" : "Salva casella Gmail"}</button>

            {gmailBoxes.length > 0 && (
              <table style={{ marginTop: 14 }}>
                <thead><tr><th>Gmail</th><th>Stato</th><th></th></tr></thead>
                <tbody>
                  {gmailBoxes.map((b) => {
                    const isRateLimit = !!(b.last_error && (b.last_error.includes("429") || /rate limit/i.test(b.last_error)));
                    const retryAt = b.retry_after ? new Date(b.retry_after) : null;
                    const inPause = isRateLimit && retryAt && retryAt > new Date();
                    return (
                    <tr key={b.id}>
                      <td>{b.address}</td>
                      <td>
                        <span className={"pill " + (b.has_oauth ? "gestita" : "da_gestire")}>{b.has_oauth ? (inPause ? "collegata · in pausa" : "collegata") : "da collegare"}</span>
                        {isRateLimit ? (
                          <div className="muted" style={{ fontSize: 11, color: "#facc15" }} data-testid={`gmail-ratelimit-${b.id}`}>
                            ⏸ Limite invii Google raggiunto (troppe email ravvicinate). La casella <b>resta collegata</b>: non serve ricollegarla, riprende da sola{retryAt ? ` ${retryAt.toDateString() === new Date().toDateString() ? "alle" : "il " + retryAt.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) + " alle"} ${retryAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : " a breve"}.
                          </div>
                        ) : b.last_error && (
                          <div className="muted" style={{ fontSize: 11 }}>{b.last_error}</div>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button className="btn sm primary" onClick={() => connectGmail(b.id)} data-testid="gmail-connect-btn">{b.has_oauth ? "Ricollega" : "Collega Gmail"}</button>{" "}
                        <button className="btn sm danger" onClick={() => removeBox(b.id)}>Elimina</button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="panel" style={{ marginTop: 18 }} data-testid="sender-config-panel">
            <h3>Casella mittente per invii automatici</h3>
            <p className="muted">Da questa casella partono: funnel/opt-in, avvisi e solleciti F24, report attività, promemoria appuntamenti via email.</p>
            <select value={senderId} onChange={(e) => saveSender(e.target.value)} data-testid="sender-config-select">
              <option value="">— Scegli la casella —</option>
              {gmailBoxes.filter((b) => b.has_oauth).map((b) => <option key={b.id} value={b.id}>{b.address}</option>)}
            </select>
          </div>

          <div className="panel" style={{ marginTop: 18 }}>
            <h3>Caselle PEC dei clienti (Aruba / Legalmail)</h3>
            <p className="muted">Il CRM si collega via IMAP alla PEC, mostra i messaggi in "Da gestire" abbinati al cliente e li inoltra all'indirizzo indicato. L'originale certificato resta nella PEC. Le password sono cifrate.</p>
            <div className="grid-2">
              <div className="field"><label>Provider</label>
                <select value={pec.provider} onChange={(e) => setPec({ ...pec, provider: e.target.value })}>
                  <option value="pec_aruba">Aruba PEC</option><option value="pec_legalmail">Legalmail (InfoCert)</option>
                </select>
              </div>
              <div className="field"><label>Indirizzo PEC</label><input placeholder="cliente@pec.it" value={pec.address} onChange={(e) => setPec({ ...pec, address: e.target.value })} /></div>
            </div>
            <div className="grid-2">
              <div className="field"><label>Username (se diverso)</label><input value={pec.username} onChange={(e) => setPec({ ...pec, username: e.target.value })} /></div>
              <div className="field"><label>Password</label><input type="password" value={pec.password} onChange={(e) => setPec({ ...pec, password: e.target.value })} /></div>
            </div>
            <div className="grid-2">
              <div className="field"><label>Inoltra a</label><input placeholder="cliente@esempio.it" value={pec.forward_to} onChange={(e) => setPec({ ...pec, forward_to: e.target.value })} /></div>
              <div className="field"><label>Cliente collegato (facoltativo)</label>
                <select value={pec.company_id} onChange={(e) => setPec({ ...pec, company_id: e.target.value })}>
                  <option value="">— nessuno —</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <button className="btn primary" onClick={addPec} disabled={busy}>{busy ? "Aggiunta…" : "Aggiungi casella PEC"}</button>
            {pecBoxes.length > 0 && (
              <table style={{ marginTop: 14 }}>
                <thead><tr><th>PEC</th><th>Provider</th><th>Inoltra a</th><th>Stato</th><th></th></tr></thead>
                <tbody>
                  {pecBoxes.map((b) => (
                    <tr key={b.id}>
                      <td>{b.address}</td>
                      <td>{b.provider === "pec_legalmail" ? "Legalmail" : "Aruba"}</td>
                      <td>{b.forward_to || "—"}</td>
                      <td><span className={"pill " + (b.status === "collegata" ? "gestita" : b.status === "errore" ? "da_gestire" : "")}>{b.status}</span>{b.last_error && <div className="muted" style={{ fontSize: 11 }}>{b.last_error}</div>}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button className="btn sm" onClick={() => connectPec(b.id)}>Verifica</button>{" "}
                        <button className="btn sm" onClick={() => syncPec(b.id)} disabled={b.status !== "collegata"}>Sincronizza</button>{" "}
                        <button className="btn sm danger" onClick={() => removeBox(b.id)}>Elimina</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {tab === "esclusi" && (
        <div className="panel">
          <h3>Regole di esclusione</h3>
          <p className="muted">Le email da questi mittenti (indirizzo o dominio) non entrano in "Da gestire". Aggiungendo una regola vengono <strong>eliminate anche le email passate</strong> di quel mittente.</p>
          <div className="toolbar">
            <select style={{ maxWidth: 160 }} value={matchType} onChange={(e) => setMatchType(e.target.value)}>
              <option value="email">Indirizzo email</option><option value="domain">Dominio</option>
            </select>
            <input style={{ maxWidth: 280 }} placeholder={matchType === "domain" ? "es. newsletter.com" : "es. spam@esempio.it"} value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRule()} />
            <button className="btn primary" onClick={addRule}>Aggiungi</button>
          </div>
          <table style={{ marginTop: 12 }}>
            <thead><tr><th>Valore</th><th>Tipo</th><th></th></tr></thead>
            <tbody>
              {rules.map((r) => (<tr key={r.id}><td>{r.value}</td><td>{r.match_type === "domain" ? "Dominio" : "Email"}</td><td><button className="btn sm danger" onClick={() => removeRule(r.id)}>Elimina</button></td></tr>))}
              {rules.length === 0 && <tr><td colSpan={3}><div className="empty">Nessuna regola.</div></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "margini" && (
        <div className="panel">
          <h3>Calcolo del margine</h3>
          <p className="muted">Margine cliente = fatturato − (ore dedicate × costo orario). Le ore derivano dai documenti gestiti e dagli appuntamenti a calendario.</p>
          <div className="toolbar">
            <div className="field" style={{ maxWidth: 220 }}><label>Costo orario dello studio (€/h)</label><input type="number" min="0" value={margin.hourly_cost} onChange={(e) => setMargin({ ...margin, hourly_cost: e.target.value })} /></div>
            <div className="field" style={{ maxWidth: 240 }}><label>Tempo medio per documento (minuti)</label><input type="number" min="0" value={margin.minutes_per_document} onChange={(e) => setMargin({ ...margin, minutes_per_document: e.target.value })} /></div>
            <div className="field" style={{ maxWidth: 220 }}><label>Tempo medio per email (minuti)</label><input type="number" min="0" value={margin.minutes_per_email ?? 5} onChange={(e) => setMargin({ ...margin, minutes_per_email: e.target.value })} /></div>
            <div className="field" style={{ maxWidth: 220 }}><label>Tempo medio per incarico (minuti)</label><input type="number" min="0" value={margin.minutes_per_task ?? 20} onChange={(e) => setMargin({ ...margin, minutes_per_task: e.target.value })} /></div>
          </div>
          <button className="btn primary" onClick={saveMargin}>Salva e ricalcola margini</button>
        </div>
      )}

      {tab === "etichette" && <Tags />}
      {tab === "notifiche" && <PushSettings />}
      {tab === "import" && <ImportPage />}
      {tab === "consensi" && <Consents />}
      {tab === "consensi-gdpr" && <ConsentAdmin />}
      {tab === "calendario" && <CalendarSettings />}
      {tab === "automazioni" && <AutomationSettings />}
      {tab === "sms" && <SmsSettings />}

      {tab === "whatsapp" && (
        <div className="panel">
          <GreenWhatsApp />
          <h3>WhatsApp Business (Meta Cloud API)</h3>
          <p className="muted">Collega il numero WhatsApp dello Studio per inviare promemoria, conferme appuntamenti e campagne. Le credenziali si ottengono in Meta for Developers → WhatsApp → API Setup.</p>
          <div className="grid-2">
            <div className="field"><label>Phone Number ID</label><input value={wa.phone_number_id} onChange={(e) => setWa({ ...wa, phone_number_id: e.target.value })} data-testid="wa-phone-id" /></div>
            <div className="field"><label>Verify Token (webhook)</label><input value={wa.verify_token} onChange={(e) => setWa({ ...wa, verify_token: e.target.value })} data-testid="wa-verify-token" /></div>
          </div>
          <div className="field"><label>Access Token {waSaved?.access_token_set && <span className="muted">(salvato: {waSaved.access_token_preview} — lascia vuoto per non modificarlo)</span>}</label><input type="password" value={wa.access_token} onChange={(e) => setWa({ ...wa, access_token: e.target.value })} placeholder={waSaved?.access_token_set ? "••••••" : ""} data-testid="wa-access-token" /></div>
          <button className="btn primary" onClick={saveWa} disabled={busy} data-testid="wa-save-btn">{busy ? "Salvataggio…" : "Salva configurazione WhatsApp"}</button>
          <p className="muted" style={{ marginTop: 10 }}>Stato: <span className={"pill " + (waSaved?.access_token_set && waSaved?.phone_number_id ? "gestita" : "da_gestire")}>{waSaved?.access_token_set && waSaved?.phone_number_id ? "configurato" : "da configurare"}</span></p>
        </div>
      )}

      {tab === "backup" && <><ArchiveStatusPanel /><BackupPanel /><AutoBackupPanel /></>}
      {tab === "cancellazioni" && <DeletionLogPanel />}
    </div>
  );
}

function DeletionLogPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/deletions").then((r) => setRows(r.data.deletions)).catch(() => setRows([])); }, []);
  const fmtDate = (iso) => {
    if (!iso) return "—";
    // i microsecondi di Python (.123456) mandano in tilt Safari: si accorciano a millisecondi
    const d = new Date(String(iso).replace(/(\.\d{3})\d+/, "$1"));
    if (isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  return (
    <div className="panel" data-testid="deletion-log-panel">
      <h3>🗑️ Registro cancellazioni</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        Ogni cancellazione di lead, aziende, persone o contatti opt-in — manuale, da API o da bonifica automatica —
        lascia una riga qui. Nessuna rimozione silenziosa.
      </p>
      {!rows ? <p className="muted">Caricamento…</p> : rows.length === 0 ? (
        <p className="muted" data-testid="deletion-log-empty">Nessuna cancellazione registrata finora.</p>
      ) : (
        <table style={{ width: "100%", marginTop: 10 }} data-testid="deletion-log-table">
          <thead><tr><th>Quando</th><th>Tipo</th><th>Nome</th><th>Email</th><th>Origine</th></tr></thead>
          <tbody>
            {rows.map((d, i) => (
              <tr key={i} data-testid={`deletion-log-row-${i}`}>
                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(d.at)}</td>
                <td>{d.tipo}</td>
                <td>{d.nome || "—"}</td>
                <td>{d.email || "—"}</td>
                <td className="muted">{d.origine}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ArchiveStatusPanel() {
  const [arch, setArch] = useState(null);
  useEffect(() => { api.get("/emails/archive-status").then((r) => setArch(r.data)).catch(() => {}); }, []);
  const fmtSpace = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${Number(mb || 0).toLocaleString("it-IT")} MB`);
  return (
    <div className="panel" data-testid="archive-status-panel">
      <h3>🔒 Archivio permanente allegati</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        Gli allegati di email e WhatsApp (ricevuti e inviati) vengono salvati per sempre nel database del CRM da un
        processo automatico ogni 30 minuti: restano scaricabili anche dopo anni, anche se scadono alla fonte.
      </p>
      {!arch ? <p className="muted">Caricamento…</p> : (
        <div style={{ display: "flex", gap: 26, flexWrap: "wrap", alignItems: "baseline" }}>
          <div><span style={{ fontSize: 26, fontWeight: 800, color: "#39ff14" }} data-testid="archive-files-count">{arch.archived_files.toLocaleString("it-IT")}</span><span className="muted" style={{ marginLeft: 8 }}>file al sicuro</span></div>
          <div><span style={{ fontSize: 26, fontWeight: 800 }} data-testid="archive-space">{fmtSpace(arch.archived_mb)}</span><span className="muted" style={{ marginLeft: 8 }}>spazio occupato</span></div>
          <div className="muted" data-testid="archive-pending">
            {arch.messages_pending > 0
              ? <>⏳ <b>{arch.messages_pending}</b> messaggi in coda di archiviazione (completati automaticamente entro poche ore)</>
              : <>✅ tutto lo storico è stato elaborato</>}
          </div>
          {arch.attachments_failed > 0 && (
            <div className="muted" data-testid="archive-failed">⚠️ {arch.attachments_failed} allegati non più recuperabili (scaduti alla fonte prima dell'archiviazione)</div>
          )}
        </div>
      )}
    </div>
  );
}

function AutoBackupPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/backups", { noCache: true }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setBusy(true); setMsg("Creo il backup…");
    try {
      const r = await api.post("/admin/backups/run");
      setMsg(`✅ Backup salvato nel database (${Math.round(r.data.backup.size / 1024)} KB).`);
      load();
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const download = async (b) => {
    try {
      const r = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob", noCache: true });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `crm_backup_scadenze_${(b.created_at || "").slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { setMsg("Errore download: " + (e.response?.data?.detail || e.message)); }
  };
  const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  return (
    <div className="panel" data-testid="auto-backup-panel">
      <h3>🗓 Backup automatico settimanale</h3>
      <p className="muted">Ogni <b>lunedì mattina</b> il CRM salva da solo <b>nel database</b> una copia dei dati di <b>checklist fiscali, Dichiarazioni Redditi e scadenze</b> (ricevi solo una notifica sulla campanella). Le ultime 12 copie restano qui sotto: puoi scaricarle e ripristinarle con "⬆ Importa backup" (pannello sopra).</p>
      <div className="toolbar" style={{ gap: 10 }}>
        <button className="btn primary" onClick={runNow} disabled={busy} data-testid="autobackup-run-btn">{busy ? "…" : "▶ Esegui backup ora"}</button>
      </div>
      {msg && <p className="muted" style={{ marginTop: 8 }} data-testid="autobackup-msg">{msg}</p>}
      {data && data.backups?.length > 0 && (
        <table style={{ width: "100%", marginTop: 10 }} data-testid="autobackup-table">
          <thead><tr><th>Data</th><th>Contenuto</th><th style={{ width: 90 }}>Dimensione</th><th style={{ width: 90 }} /></tr></thead>
          <tbody>
            {data.backups.map((b) => (
              <tr key={b.id}>
                <td>{fmtDT(b.created_at)}</td>
                <td className="muted" style={{ fontSize: 12 }}>{b.counts ? `${b.counts.redditi_rows ?? 0} righe Redditi · ${b.counts.fiscal_checklist ?? 0} spunte checklist · ${b.counts.scadenze_done ?? 0} scadenze gestite` : "—"}</td>
                <td>{Math.round((b.size || 0) / 1024)} KB</td>
                <td><button className="btn sm" onClick={() => download(b)} data-testid={`autobackup-dl-${b.id}`}>⬇ Scarica</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data && !data.backups?.length && <p className="muted" style={{ marginTop: 8 }}>Nessun backup automatico ancora creato: il primo verrà generato lunedì mattina (o premi "Esegui backup ora").</p>}
    </div>
  );
}

function BackupPanel() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [revMsg, setRevMsg] = useState("");
  const [storicoSince, setStoricoSince] = useState("");
  const [storicoCount, setStoricoCount] = useState(null);
  const [storicoMsg, setStoricoMsg] = useState("");
  const countStorico = async () => {
    setStoricoMsg("Conteggio in corso…");
    try {
      const r = await api.get(`/emails/storico-archived${storicoSince ? `?since=${storicoSince}` : ""}`);
      setStoricoCount(r.data.count);
      setStoricoMsg("");
    } catch (e) { setStoricoMsg("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const restoreStorico = async () => {
    if (!window.confirm(`Riportare in "Da gestire" ${storicoCount ?? "le"} email archiviate automaticamente dallo storico? Quelle già abbinate a cartelle restano gestite. Potrai poi archiviare in blocco newsletter e notifiche con i filtri.`)) return;
    setBusy(true); setStoricoMsg("Ripristino in corso…");
    try {
      const r = await api.post("/emails/restore-storico", { since: storicoSince || null });
      setStoricoMsg(`✅ ${r.data.restored} email riportate in "Da gestire".`);
      setStoricoCount(null);
    } catch (e) { setStoricoMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const markReviewers = async () => {
    if (!window.confirm("Marcare come 'recensione già lasciata' i clienti che ti hanno già recensito su Google? È ripetibile senza rischi.")) return;
    setBusy(true); setRevMsg("Analisi anagrafiche in corso…");
    try {
      const r = await api.post("/admin/mark-reviewers");
      const d = r.data;
      let t = `✅ Marcate ${d.persone_marcate} persone + ${d.aziende_marcate} aziende.`;
      if (d.non_trovati?.length) t += `\n\nNon in anagrafica (nessuna azione): ${d.non_trovati.join(", ")}.`;
      setRevMsg(t);
    } catch (e) { setRevMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const exportData = async () => {
    setBusy(true); setMsg("Preparo il backup…");
    try {
      const r = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crm_backup_${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      setMsg("Backup scaricato. Conservalo: ti servirà per portare i dati in produzione dopo il deploy.");
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const importData = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm("Importare i dati da questo file? I record esistenti con lo stesso ID verranno aggiornati, gli altri aggiunti. Nessun dato verrà cancellato.")) return;
    setBusy(true); setMsg("Importazione in corso…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const tot = Object.values(r.data.imported || {}).reduce((a, b) => a + b, 0);
      setMsg(`Import completato: ${tot} record ripristinati.`);
    } catch (e2) { setMsg("Errore: " + (e2.response?.data?.detail || e2.message)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="panel" data-testid="backup-panel">
      <h3>Backup e migrazione dati</h3>
      <p className="muted">L'anteprima e l'app pubblicata (deploy) usano database separati. Per avere tutti i tuoi dati in produzione: <b>1)</b> qui in anteprima scarica il backup, <b>2)</b> dopo il deploy accedi all'app pubblicata e importa il file. I dati salvati restano poi persistenti a ogni aggiornamento.</p>
      <div className="toolbar" style={{ gap: 10 }}>
        <button className="btn primary" onClick={exportData} disabled={busy} data-testid="backup-export-btn">⬇ Scarica backup completo</button>
        <label className="btn" style={{ cursor: "pointer" }} data-testid="backup-import-label">
          ⬆ Importa backup
          <input type="file" accept="application/json,.json" style={{ display: "none" }} onChange={importData} data-testid="backup-import-input" />
        </label>
      </div>
      {msg && <p className="muted" style={{ marginTop: 10 }} data-testid="backup-msg">{msg}</p>}

      <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Email archiviate dallo storico</h3>
        <p className="muted">Durante il primo import le email <b>già lette su Gmail</b> (anche solo dal telefono) sono state archiviate come "gestite (storico)" senza passare da "Da gestire". Da qui puoi riportarle in "Da gestire" per lavorarle nel CRM. Le email già abbinate a cartelle non vengono toccate.</p>
        <div className="toolbar" style={{ gap: 10, flexWrap: "wrap" }}>
          <span className="muted">Ricevute dal</span>
          <input type="date" value={storicoSince} onChange={(e) => { setStoricoSince(e.target.value); setStoricoCount(null); }} style={{ maxWidth: 170 }} data-testid="storico-since" />
          <button className="btn" onClick={countStorico} disabled={busy} data-testid="storico-count-btn">Conta</button>
          {storicoCount != null && <span data-testid="storico-count">{storicoCount} email archiviate dallo storico</span>}
          <button className="btn primary" onClick={restoreStorico} disabled={busy || storicoCount == null || storicoCount === 0} data-testid="storico-restore-btn">↩ Riporta in Da gestire</button>
        </div>
        {storicoMsg && <p className="muted" style={{ marginTop: 10 }} data-testid="storico-msg">{storicoMsg}</p>}
      </div>

      <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Recensioni già lasciate</h3>
        <p className="muted">Marca automaticamente i clienti che hanno già lasciato una recensione Google (lista storica) come "recensione già lasciata", così non ricevono più la richiesta. Da usare <b>dopo il deploy</b>, direttamente sull'app pubblicata (è ripetibile senza rischi).</p>
        <button className="btn primary" onClick={markReviewers} disabled={busy} data-testid="mark-reviewers-btn">⭐ Applica recensioni già lasciate</button>
        {revMsg && <p className="muted" style={{ marginTop: 10, whiteSpace: "pre-wrap" }} data-testid="mark-reviewers-msg">{revMsg}</p>}
      </div>
    </div>
  );
}
